import { useRef } from 'react';
import { resolveCreativePreview } from '@/features/campaigns/utils/resolveCreativePreview';
import {
  MAX_CAMPAIGN_AMOUNT,
  MIN_CAMPAIGN_AMOUNT,
} from '@/features/campaigns/constants/campaignOptions';
import { useBillboardRows } from './useBillboardRows';
import { useAltCreativeUpload } from './useAltCreativeUpload';
import { useCheckoutColumns } from './useCheckoutColumns';
import { OrientationWarningBanner } from './sections/OrientationWarningBanner';
import { OverviewSection } from './sections/OverviewSection';
import { SelectedScreensTable } from './sections/SelectedScreensTable';
import { LaunchSummaryAside } from './sections/LaunchSummaryAside';
import { CouponSection } from './sections/CouponSection';
import { resolvePreviewBill } from './previewBilling';
import { useManualPreview } from '@/features/campaigns/components/ManualBuilder/manualPreviewContext';
import type { PreviewStepProps } from './PreviewStep.types';

export function PreviewStep({
  data,
  campaignData,
  creativePreview,
  availableBalance,
  selectedScreensMeta = {},
  altMediaIds,
  setAltMediaIds,
  onLaunch,
  isLaunching = false,
  isRelaunch = false,
  onBack,
  coupon,
  onOpenPreview,
  bookingSlotCount,
}: PreviewStepProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const twoColumn = useCheckoutColumns(shellRef, 920);
  const manualPreview = useManualPreview();
  const openPreview = onOpenPreview || (() => manualPreview.openPreview());

  const bill = resolvePreviewBill(data);
  const cost = bill.amountWithTax;
  const exceedsMaxOrder = cost > MAX_CAMPAIGN_AMOUNT;
  const belowMinOrder = cost > 0 && cost < MIN_CAMPAIGN_AMOUNT;
  const analytics = (data.analytics || {}) as Record<string, unknown>;
  const displayPreview = resolveCreativePreview(
    creativePreview,
    campaignData,
    data,
  );

  const screens = useBillboardRows(data, selectedScreensMeta);

  const {
    requiredLabel,
    warningCopy,
    currentMediaOrientation,
    showOrientationWarning: rawShowOrientationWarning,
    uploadTargets,
    altPreviewByOrientation,
    uploadingOrientation,
    acceptedAiConvert,
    setAcceptedAiConvert,
    fileInputRef,
    openUpload,
    handleAltUpload,
    dropUpload,
    removeAlt,
    allUploadsReady,
    bannerResolved,
  } = useAltCreativeUpload({ data, campaignData, altMediaIds, setAltMediaIds });

  const showOrientationWarning = rawShowOrientationWarning && !isRelaunch;

  const handleContinueWithAiConvert = () => {
    setAcceptedAiConvert(true);
    onLaunch();
  };

  const payment = (
    <LaunchSummaryAside
      data={data}
      availableBalance={availableBalance}
      exceedsMaxOrder={exceedsMaxOrder}
      belowMinOrder={belowMinOrder}
      onBack={onBack}
      sticky={twoColumn}
      screenCount={screens.length}
      totalPlays={
        screens.some((r) => r.reps != null)
          ? screens.reduce((sum, r) => sum + (r.reps || 0), 0)
          : undefined
      }
    />
  );

  return (
    <div
      ref={shellRef}
      className="camp-fade-up flex w-full flex-col gap-3 text-left"
    >
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-success)] uppercase">
            Ready to launch
          </p>
          <span className="rounded-md bg-[var(--dash-soft)] px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums text-[var(--dash-muted)]">
            Step 4 of 4
          </span>
        </div>
        <h1 className="m-0 mt-1 text-[20px] font-semibold tracking-tight text-[var(--dash-ink)] sm:text-[22px]">
          Review &amp; pay
        </h1>
        <p className="mt-1 mb-0 max-w-2xl text-[12.5px] leading-relaxed text-[var(--dash-muted)]">
          Confirm screens and offers, review the amount due, then pay from the
          footer. Open Live Preview anytime without changing this layout.
        </p>
      </header>

      {showOrientationWarning ? (
        <OrientationWarningBanner
          creativePreview={displayPreview}
          bannerResolved={bannerResolved}
          allUploadsReady={allUploadsReady}
          requiredLabel={requiredLabel}
          warningTitle={warningCopy.title}
          warningBody={warningCopy.body}
          currentMediaOrientation={currentMediaOrientation}
          uploadTargets={uploadTargets}
          altPreviewByOrientation={altPreviewByOrientation}
          uploadingOrientation={uploadingOrientation}
          acceptedAiConvert={acceptedAiConvert}
          isLaunching={isLaunching}
          fileInputRef={fileInputRef}
          onFileChange={handleAltUpload}
          onOpenUpload={openUpload}
          onDropUpload={dropUpload}
          onRemoveAlt={removeAlt}
          onContinueWithAiConvert={handleContinueWithAiConvert}
        />
      ) : null}

      <OverviewSection
        data={data}
        campaignData={campaignData}
        creativePreview={displayPreview}
        analytics={analytics}
        screenCount={screens.length}
        bookingSlotCount={bookingSlotCount}
        onOpenPreview={manualPreview.previewOpen ? undefined : openPreview}
      />

      {twoColumn ? (
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(300px,340px)] items-start gap-3">
          <div className="flex min-w-0 flex-col gap-3">
            <SelectedScreensTable screens={screens} />
            {coupon ? <CouponSection {...coupon} /> : null}
          </div>
          <div className="min-w-0">{payment}</div>
        </div>
      ) : (
        <div className="flex w-full max-w-3xl flex-col gap-3 xl:max-w-none">
          <SelectedScreensTable screens={screens} />
          {coupon ? <CouponSection {...coupon} /> : null}
          <div className="w-full">{payment}</div>
        </div>
      )}
    </div>
  );
}
